import React, { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useResponsive } from "react-hooks-responsive";
import { Link } from "react-router-dom";
import {
  Grid,
  Col,
  Row,
  Form,
  FormGroup,
  FormControl,
  ControlLabel,
  Button,
} from "react-bootstrap";
import Radio from "../../components/CustomRadio/CustomRadio.jsx";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import MandatoryLabel from "../Components/MandatoryLabel.jsx";
import getLookups from "../Config/lookups.js";
import { generateNormalizer } from "../Config/utils.js";
import { constants } from "../../redux/questionnaire/actions.js";
import { commonConstants } from "../../redux/common/actions.js";
import { getFormattedUser } from "../../services/auth.js";
import NotificationSystem from "react-notification-system";
import { style as notificationStyling } from "../../variables/Variables.jsx";
import DatetimePicker from "views/Components/DatePicker.jsx";
import { hitBopOptions, allowedCallEntryTypes } from "./CallEntryFormOptions.jsx";
import { callEntryTypes } from "../Config/lookups.js";
import { checkTrue, checkFalse } from "./utils.js";

const { UPDATE_QUESTIONNAIRE_FORM_FIELD, RESET_FORM_FIELDS, SUBMIT_FORM } =
  constants;
const { REQUEST } = commonConstants;
const {
  veteranRanks,
  callerStruggles,
  callerTypes,
  coplineReferralReasons,
  impairmentReasons,
  militaryBranches,
  countries,
  headTraumas,
} = getLookups();
const normalizeHeadTrauma = generateNormalizer("headTraumas");
const states = require("../Config/usStates.json");
const provinces = require("../Config/canadaProvinces.json");
const australiaStates = require("../Config/australiaStates.json");

const Questionnaire = () => {
  const { loading, success, error, ...form } = useSelector(
    (state) => state.questionnaire
  );
  const dispatch = useDispatch();
  // initial page fetch - has a dependency due to browser fun, normally this is empty(navigating from one report to another does not trigger fetch when empty)
  useEffect(() => {
    setNotificationSystem(notificationSystem);
    return () => {};
  }, []);
  // retrieve user
  const [callTakerId, setCallTakerId] = useState(null);
  useEffect(() => {
    async function getAuth() {
      const user = await getFormattedUser();
      setCallTakerId(user.id);
    }
    getAuth().then();
  }, []);
  // handle ajax side effects
  useEffect(() => {
    const notificationSystem = _notificationSystem.current;
    if (notificationSystem && success.message) {
      notificationSystem.addNotification({
        message: success.message,
        level: "info",
        position: "tc",
      });
    }
    if (notificationSystem && error.message) {
      notificationSystem.addNotification({
        message: error.message,
        level: "error",
        position: "tc",
      });
    }
    return () => {};
  }, [success.message, error.message]);
  // handle scroll to top for notifications
  useEffect(() => {
    // THIS IS SCREWY - Desktop works fine, Mobile scrolls to top quickly then back to original position - 2x fixed this
    document.getElementById("top-of-the-form-to-ya").scrollIntoView(true);
    document.getElementById("top-of-the-form-to-ya").scrollIntoView(true);
    return () => {};
  }, [success.message, error.message]);

  const [_notificationSystem, setNotificationSystem] = useState({});

  let {
    callEntryType,
    name,
    email,
    cellPhone,
    homePhone,
    isFrequentCaller,
    isVeteran,
    veteranBranch,
    yearsOfMilitaryService,
    isDisabled,
    receivedVaTreatment,
    gender,
    veteranRank,
    yearsOfPoliceService,
    country,
    provinceState,
    city,
    departmentSize,
    callerType,
    age,
    coplineReferral,
    headTraumaCheck,
    headTrauma,
    impairment,
    currentStruggle,
    callStart,
    callEnd,
    callReason,
    isSuicidalCall,
    callWasReferred,
    referralDetails,
    returnCall,
    returnCallDetails,
    returnCallFollowUpNotes,
    originCallReportId,
    callFulfilled,
    callType,
    returnCallDate,
    id,
    resolvedReturnCalls = [],
    unResolvedReturnCalls = [],
    combatExperience,
    treatedForPtsd,
    otherMedicalCondition,
  } = form;
  const formattedPayload = {
    name,
    email,
    cellPhone,
    homePhone,
    isFrequentCaller,
    isVeteran,
    veteranBranch,
    yearsOfMilitaryService,
    isDisabled,
    receivedVaTreatment,
    gender,
    veteranRank,
    yearsOfPoliceService,
    country,
    provinceState,
    city,
    departmentSize,
    callerType,
    age,
    coplineReferral,
    headTraumaCheck,
    headTrauma,
    impairment,
    currentStruggle,
    callStart,
    callEnd,
    callReason,
    isSuicidalCall,
    callWasReferred,
    referralDetails,
    returnCall,
    returnCallDetails,
    returnCallFollowUpNotes,
    returnCallDate,
    callTakerId,
    originCallReportId,
    id,
    callType,
    callEntryType,
    callFulfilled,
    combatExperience,
    treatedForPtsd,
    otherMedicalCondition,
  };

  const countryStateLabelMap = { USA: "State/Territory", CANADA: "Province/Territory", AUSTRALIA: "State/Territory" };
  const countryStateLabel = countryStateLabelMap[country?.value] || "State/Territory";

  const provinceStateCollectionMap = { USA: states, CANADA: provinces, AUSTRALIA: australiaStates };
  const provinceStateCollection = provinceStateCollectionMap[country?.value] || states;

  const breakpoints = { xs: 0, sm: 425, md: 1024 };
  const { size } = useResponsive(breakpoints);
  const radioStyle = size === "xs" ? { transform: "scale(3)", margin: 16 } : {};
  const notificationSystem = useRef();
  const legitCall = callEntryType ? callEntryType.value === callEntryTypes[0].value : false;
  const coplineMandatoryField = (title) => {
    return legitCall ? (
      <MandatoryLabel title={title} />
    ) : (
      <ControlLabel>{title}</ControlLabel>
    );
  };

  // const submitDisabled = useMemo(() => legitCall && (name && name.length === 0 || callReason && callReason.length === 0), [legitCall, name, callReason])

  return (
    <div className="main-content">
      <NotificationSystem
        ref={notificationSystem}
        style={notificationStyling}
      />
      <Form>
        <Grid fluid>
          <Row>
            <Col xs={12} sm={12}>
              <FormGroup>
                <ControlLabel>Call Entry Type</ControlLabel>
                <Select
                  options={allowedCallEntryTypes.map((type) => {
                    return { label: type.label, value: type.value };
                  })}
                  value={callEntryType}
                  isSearchable={true}
                  name="callEntryType"
                  onChange={(event) =>
                    handleChange(dispatch, event, "callEntryType")
                  }
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col xs={12} sm={6}>
              <FormGroup>
                {coplineMandatoryField("Caller Name")}
                <FormControl
                  type="text"
                  id="name"
                  name="name"
                  value={name}
                  onChange={(event) => handleChange(dispatch, event)}
                />
              </FormGroup>
            </Col>
            <Col xs={12} sm={6}>
              <FormGroup>
                <ControlLabel>Caller Email</ControlLabel>
                <FormControl
                  type="text"
                  id="email"
                  name="email"
                  value={email}
                  onChange={(event) => handleChange(dispatch, event)}
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col xs={12} sm={6}>
              <FormGroup>
                {coplineMandatoryField("Caller Area Code")}
                <FormControl
                  type="text"
                  id="cell-phone"
                  name="cellPhone"
                  value={cellPhone}
                  onChange={(event) => handleChange(dispatch, event)}
                />
              </FormGroup>
            </Col>
            <Col xs={12} sm={6}>
              <FormGroup>
                <ControlLabel>Caller Phone</ControlLabel>
                <FormControl
                  type="text"
                  id="home-phone"
                  name="homePhone"
                  value={homePhone}
                  onChange={(event) => handleChange(dispatch, event)}
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col xs={12} sm={6}>
              <FormGroup>
                {coplineMandatoryField("Suicidal Call")}
                <Radio
                  number="isSuicidalCall-true"
                  option={true}
                  name="isSuicidalCall"
                  onChange={(event) => handleChange(dispatch, event)}
                  checked={checkTrue(isSuicidalCall)}
                  label="Yes"
                  style={radioStyle}                 
                />
                <Radio
                  number="isSuicidalCall-false"
                  option={false}
                  name="isSuicidalCall"
                  onChange={(event) => handleChange(dispatch, event)}
                  checked={checkFalse(isSuicidalCall)}
                  label="No"
                  style={radioStyle}
                />
              </FormGroup>
            </Col>
            <Col xs={12} sm={6}>
              <FormGroup>
                <ControlLabel> Frequent/Chronic Caller </ControlLabel>
                <Radio
                  number="isFrequentCaller-true"
                  option={true}
                  name="isFrequentCaller"
                  onChange={(event) => handleChange(dispatch, event)}
                  checked={checkTrue(isFrequentCaller)}
                  label="Yes"
                  style={radioStyle}
                />
                <Radio
                  number="isFrequentCaller-false"
                  option={false}
                  name="isFrequentCaller"
                  onChange={(event) => handleChange(dispatch, event)}
                  checked={checkFalse(isFrequentCaller)}
                  label="No"
                  style={radioStyle}
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col xs={12} sm={6}>
              <FormGroup>
                <ControlLabel>Disabled</ControlLabel>
                <Radio
                  number="isDisabled-true"
                  option={true}
                  name="isDisabled"
                  onChange={(event) => handleChange(dispatch, event)}
                  checked={checkTrue(isDisabled)}
                  label="Yes"
                  style={radioStyle}
                />
                <Radio
                  number="isDisabled-false"
                  option={false}
                  name="isDisabled"
                  onChange={(event) => handleChange(dispatch, event)}
                  checked={checkFalse(isDisabled)}
                  label="No"
                  style={radioStyle}
                />
              </FormGroup>
            </Col>
            <Col xs={12} sm={6}>
              <FormGroup>
                <ControlLabel>Gender</ControlLabel>
                <Radio
                  number="gender-male"
                  option="male"
                  name="gender"
                  onChange={(event) => handleChange(dispatch, event)}
                  checked={gender === "male"}
                  label="Male"
                  style={radioStyle}
                />
                <Radio
                  number="gender-female"
                  option="female"
                  name="gender"
                  onChange={(event) => handleChange(dispatch, event)}
                  checked={gender === "female"}
                  label="Female"
                  style={radioStyle}
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col xs={12} sm={6}>
              <FormGroup>
                <ControlLabel>Diagnosed/Treated for PTSD</ControlLabel>
                <Radio
                  number="treatedForPtsd-true"
                  option={true}
                  name="treatedForPtsd"
                  onChange={(event) => handleChange(dispatch, event)}
                  checked={checkTrue(treatedForPtsd)}
                  label="Yes"
                  style={radioStyle}
                />
                <Radio
                  number="treatedForPtsd-false"
                  option={false}
                  name="treatedForPtsd"
                  onChange={(event) => handleChange(dispatch, event)}
                  checked={checkFalse(treatedForPtsd)}
                  label="No"
                  style={radioStyle}
                />
              </FormGroup>
            </Col>
            <Col xs={12} sm={6}>
              <FormGroup>
                <ControlLabel>
                  Diagnosed/Treated Other Medical Condition
                </ControlLabel>
                <Radio
                  number="otherMedicalCondition-true"
                  option={true}
                  name="otherMedicalCondition"
                  onChange={(event) => handleChange(dispatch, event)}
                  checked={checkTrue(otherMedicalCondition)}
                  label="Yes"
                  style={radioStyle}
                />
                <Radio
                  number="otherMedicalCondition-false"
                  option={false}
                  name="otherMedicalCondition"
                  onChange={(event) => handleChange(dispatch, event)}
                  checked={checkFalse(otherMedicalCondition)}
                  label="No"
                  style={radioStyle}
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col xs={12} sm={6}>
              <FormGroup>
                <ControlLabel>Veteran</ControlLabel>
                <Radio
                  number="is-veteran-1"
                  option={true}
                  name="isVeteran"
                  onChange={(event) =>
                    handleChange(dispatch, event, "isVeteran")
                  }
                  checked={checkTrue(isVeteran)}
                  label="Yes"
                  style={radioStyle}
                />
                <Radio
                  number="is-veteran-2"
                  option={false}
                  name="isVeteran"
                  onChange={(event) =>
                    handleChange(dispatch, event, "isVeteran")
                  }
                  checked={checkFalse(isVeteran)}
                  label="No"
                  style={radioStyle}
                />
              </FormGroup>
            </Col>
            {isVeteran && (
              <Col xs={12} sm={6}>
                <FormGroup>
                  <ControlLabel>Branch</ControlLabel>
                  <Select
                    options={militaryBranches.map((branch) => {
                      return { label: branch.label, value: branch.value };
                    })}
                    value={veteranBranch}
                    isSearchable={true}
                    name="veteranBranch"
                    onChange={(event) =>
                      handleChange(dispatch, event, "veteranBranch")
                    }
                  />
                </FormGroup>
              </Col>
            )}
            <Col xs={12} sm={6}>
              <FormGroup>
                <ControlLabel>Combat Experience</ControlLabel>
                <Radio
                  number="combatExperience-true"
                  option={true}
                  name="combatExperience"
                  onChange={(event) => handleChange(dispatch, event)}
                  checked={checkTrue(combatExperience)}
                  label="Yes"
                  style={radioStyle}
                />
                <Radio
                  number="combatExperience-false"
                  option={false}
                  name="combatExperience"
                  onChange={(event) => handleChange(dispatch, event)}
                  checked={checkFalse(combatExperience)}
                  label="No"
                  style={radioStyle}
                />
              </FormGroup>
            </Col>
          </Row>
          {isVeteran && (
            <Row>
              <Col xs={12} sm={6}>
                <FormGroup>
                  <ControlLabel>Years of Military Service</ControlLabel>
                  <FormControl
                    type="number"
                    id="years-of-military-service"
                    name="yearsOfMilitaryService"
                    value={yearsOfMilitaryService}
                    onChange={(event) => handleChange(dispatch, event)}
                  />
                </FormGroup>
              </Col>
              <Col xs={12} sm={6}>
                <FormGroup>
                  <ControlLabel>VA Treatment</ControlLabel>
                  <Radio
                    number="received-va-treatment-1"
                    option={true}
                    name="receivedVaTreatment"
                    onChange={(event) =>
                      handleChange(dispatch, event, "receivedVaTreatment")
                    }
                    checked={checkTrue(receivedVaTreatment)}
                    label="Yes"
                    style={radioStyle}
                  />
                  <Radio
                    number="received-va-treatment-2"
                    option={false}
                    name="receivedVaTreatment"
                    onChange={(event) =>
                      handleChange(dispatch, event, "receivedVaTreatment")
                    }
                    checked={checkFalse(receivedVaTreatment)}
                    label="No"
                    style={radioStyle}
                  />
                </FormGroup>
              </Col>
            </Row>
          )}
          <Row>
            <Col xs={12} sm={6}>
              <FormGroup>
                <ControlLabel>Rank</ControlLabel>
                <Select
                  options={veteranRanks.map((rank) => {
                    return { label: rank.label, value: rank.value };
                  })}
                  value={veteranRank}
                  isSearchable={true}
                  name="veteranRank"
                  onChange={(event) =>
                    handleChange(dispatch, event, "veteranRank")
                  }
                />
              </FormGroup>
            </Col>
            <Col xs={12} sm={6}>
              <FormGroup>
                <ControlLabel>Years of Service</ControlLabel>
                <FormControl
                  type="number"
                  id="years-of-police-service"
                  name="yearsOfPoliceService"
                  placeholder="please enter a number..."
                  value={yearsOfPoliceService}
                  onChange={(event) => handleChange(dispatch, event)}
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col xs={12} sm={4}>
              <FormGroup>
                <ControlLabel>Country</ControlLabel>
                <Select
                  options={countries}
                  defaultValue={countries[0]}
                  value={country}
                  isSearchable={true}
                  name="country"
                  onChange={(event) => handleChange(dispatch, event, "country")}
                />
              </FormGroup>
            </Col>
            <Col xs={12} sm={4}>
              <FormGroup>
                <ControlLabel>{countryStateLabel}</ControlLabel>
                <Select
                  options={provinceStateCollection}
                  value={provinceState}
                  isSearchable={true}
                  name="province-state"
                  onChange={(event) =>
                    handleChange(dispatch, event, "provinceState")
                  }
                />
              </FormGroup>
            </Col>
            <Col xs={12} sm={4}>
              <FormGroup>
                <ControlLabel>City</ControlLabel>
                <FormControl
                  type="text"
                  id="city"
                  name="city"
                  value={city}
                  onChange={(event) => handleChange(dispatch, event)}
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col xs={12} sm={6}>
              <FormGroup>
                <ControlLabel>Department Size</ControlLabel>
                <FormControl
                  type="text"
                  id="department-size"
                  name="departmentSize"
                  value={departmentSize}
                  onChange={(event) => handleChange(dispatch, event)}
                />
              </FormGroup>
            </Col>
            <Col xs={12} sm={6}>
              <FormGroup>
                <ControlLabel>Caller Type</ControlLabel>
                <Select
                  options={callerTypes.map((type) => {
                    return { label: type.label, value: type.value };
                  })}
                  value={callerType}
                  isSearchable={true}
                  name="callerType"
                  onChange={(event) =>
                    handleChange(dispatch, event, "callerType")
                  }
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col xs={12} sm={6}>
              <FormGroup>
                <ControlLabel>Age</ControlLabel>
                <FormControl
                  type="number"
                  id="age"
                  name="age"
                  value={age}
                  min={1}
                  max={150}
                  step={1}
                  placeholder="please enter a whole number..."
                  onChange={(event) => handleChange(dispatch, event, "age")}
                />
              </FormGroup>
            </Col>
            <Col xs={12} sm={6}>
              <FormGroup>
                {coplineMandatoryField("Heard About Copline")}
                <Select
                  options={coplineReferralReasons.map((reason) => {
                    return { label: reason.label, value: reason.value };
                  })}
                  value={coplineReferral}
                  isSearchable={true}
                  name="coplineReferral"
                  onChange={(event) =>
                    handleChange(dispatch, event, "coplineReferral")
                  }
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col xs={12} sm={6}>
              <FormGroup>
                {coplineMandatoryField("Presenting Issue")}
                <Select
                  options={callerStruggles.map((struggle) => {
                    return { label: struggle.label, value: struggle.value };
                  })}
                  value={currentStruggle}
                  isSearchable={true}
                  name="currentStruggle"
                  onChange={(event) =>
                    handleChange(dispatch, event, "currentStruggle")
                  }
                />
              </FormGroup>
            </Col>
            <Col xs={12} sm={6} style={{ border: "1px solid red" }}>
              <FormGroup>
                {coplineMandatoryField("Repetitive Head Impacts (RHI) and/or Head Impacts and Trauma (HIT) and/or Blast Overpressure (BOP) to the Head/Neck")}
                  {hitBopOptions.map(({ id, value, label }) => (
                    <Radio
                      key={id}
                      number={id}
                      option={value}
                      name="headTraumaCheck"
                      onChange={(event) => handleChange(dispatch, event)}
                      checked={headTraumaCheck === value}
                      label={label}
                      style={radioStyle}
                    />
                  ))}

                {headTraumaCheck === "reported" && (
                  <CreatableSelect
                    options={headTraumas.map((headTrauma) => {
                      return {
                        label: headTrauma.label,
                        value: headTrauma.value,
                      };
                    })}
                    isMulti
                    value={headTrauma}
                    isDisabled={headTraumaCheck !== "reported"}
                    isSearchable={true}
                    placeholder="Select all that apply and/or create new entry..."
                    name="headTrauma"
                    onChange={(event) =>
                      handleChange(dispatch, event, "headTrauma")
                    }
                  />
                )}
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col xs={12} sm={4}>
              <FormGroup>
                <ControlLabel>Impairment</ControlLabel>
                <Select
                  options={impairmentReasons.map((reason) => {
                    return { label: reason.label, value: reason.value };
                  })}
                  value={impairment}
                  isSearchable={true}
                  name="impairment"
                  onChange={(event) =>
                    handleChange(dispatch, event, "impairment")
                  }
                />
              </FormGroup>
            </Col>
            <Col xs={12} sm={4}>
              <FormGroup>
                <ControlLabel>Start Time</ControlLabel>
                <DatetimePicker
                  field="callStart"
                  value={callStart ? new Date(callStart) : new Date()}
                  handleChange={(event) =>
                    handleChange(dispatch, event, "callStart")
                  }
                />
              </FormGroup>
            </Col>
            <Col xs={12} sm={4}>
              <FormGroup>
                <ControlLabel>End Time</ControlLabel>
                <DatetimePicker
                  field="callEnd"
                  value={callEnd ? new Date(callEnd) : new Date()}
                  handleChange={(event) =>
                    handleChange(dispatch, event, "callEnd")
                  }
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col xs={12} sm={6}>
              <FormGroup>
                <ControlLabel>
                  Caller Referral - Were resources provided during the call?
                </ControlLabel>
                <Radio
                  number="call-was-referred-1"
                  option={true}
                  name="callWasReferred"
                  onChange={(event) =>
                    handleChange(dispatch, event, "callWasReferred")
                  }
                  checked={checkTrue(callWasReferred)}
                  label="Yes"
                  style={radioStyle}
                />
                <Radio
                  number="call-was-referred-2"
                  option={false}
                  name="callWasReferred"
                  onChange={(event) =>
                    handleChange(dispatch, event, "callWasReferred")
                  }
                  checked={checkFalse(callWasReferred)}
                  label="No"
                  style={radioStyle}
                />
              </FormGroup>
            </Col>
            {callWasReferred && (
              <Col xs={12} sm={12}>
                <FormGroup>
                  <ControlLabel>Resources Provided (Clinician / Organization Name(s))</ControlLabel>
                  <FormControl
                    type="textarea"
                    id="referral-details"
                    name="referralDetails"
                    value={referralDetails}
                    onChange={(event) => handleChange(dispatch, event)}
                  />
                </FormGroup>
              </Col>
            )}
          </Row>
          <Row>
            <Col sm={12}>
              <FormGroup>
                {coplineMandatoryField("Summary Narrative")}
                <FormControl
                  componentClass="textarea"
                  id="call-reason"
                  name="callReason"
                  value={callReason}
                  style={{ height: "200px" }}
                  onChange={(event) => handleChange(dispatch, event)}
                />
              </FormGroup>
            </Col>
          </Row>

          <h4 className="title">Return Call</h4>
          <Row>
            <Col xs={12} sm={4}>
              <FormGroup>
                <ControlLabel>
                  Did the caller indicate he/she would like a return call?
                </ControlLabel>
                <Radio
                  number="return-call-1"
                  option={true}
                  name="returnCall"
                  onChange={(event) =>
                    handleChange(dispatch, event, "returnCall")
                  }
                  checked={checkTrue(returnCall)}
                  label="Yes"
                  style={radioStyle}
                />
                <Radio
                  number="return-call-2"
                  option={false}
                  name="returnCall"
                  onChange={(event) =>
                    handleChange(dispatch, event, "returnCall")
                  }
                  checked={checkFalse(returnCall)}
                  label="No"
                  style={radioStyle}
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            {returnCall && (
              <>
                <Col xs={12} sm={4}>
                  <FormGroup>
                    <MandatoryLabel title="Return Call Date" />
                    <DatetimePicker
                      name="returnCallDate"
                      value={
                        returnCallDate ? new Date(returnCallDate) : new Date()
                      }
                      handleChange={(event) =>
                        handleChange(dispatch, event, "returnCallDate")
                      }
                    />
                  </FormGroup>
                </Col>
              </>
            )}
          </Row>

          {returnCall && (
            <Row>
              <Col xs={12} sm={12}>
                <FormGroup>
                  <ControlLabel>Call Back Details</ControlLabel>
                  <FormControl
                    componentClass="textarea"
                    id="return-call-details"
                    name="returnCallDetails"
                    value={returnCallDetails}
                    style={{ height: "200px" }}
                    onChange={(event) => handleChange(dispatch, event)}
                  />
                </FormGroup>
              </Col>
            </Row>
          )}
          <Row style={{ marginTop: 20 }}>
            <Col xs={12} sm={12}>
              <Button
                style={{ marginRight: "15px" }}
                bsStyle="primary"
                bsSize="lg"
                // disabled={submitDisabled}
                onClick={() => {
                  dispatch({
                    type: SUBMIT_FORM + REQUEST,
                    payload: formattedPayload,
                  });
                }}
              >
                Submit
              </Button>
              <Button
                bsStyle="warning"
                type="reset"
                bsSize="lg"
                onClick={() =>
                  dispatch({
                    type: RESET_FORM_FIELDS,
                    payload: { showMessage: true },
                  })
                }
              >
                Reset
              </Button>
            </Col>
          </Row>
          <div>
            {unResolvedReturnCalls.length > 0 && (
              <>
                <h4 className="title">Un-Resolved Return Calls</h4>
                <Row>
                  <Col xs={2}>
                    <u>Link</u>
                  </Col>
                  <Col xs={10}>
                    <u>Call Back Details</u>
                  </Col>
                </Row>
              </>
            )}
            {unResolvedReturnCalls.length > 0 &&
              unResolvedReturnCalls.map((call) => {
                return (
                  <>
                    <Row>
                      <Col xs={2}>
                        <Link to={`/admin/return-call/${call.id}`}>
                          View Return Call
                        </Link>
                      </Col>
                      <Col xs={10}>{call.safeReturnCallDetails}</Col>
                    </Row>
                  </>
                );
              })}
            {resolvedReturnCalls.length > 0 && (
              <>
                <h4 className="title">Resolved Return Calls</h4>
                <Row>
                  <Col xs={2}>
                    <u>Link</u>
                  </Col>
                  <Col xs={10}>
                    <u>Follow Up Notes</u>
                  </Col>
                </Row>
              </>
            )}
            {resolvedReturnCalls.length > 0 &&
              resolvedReturnCalls.map((call) => {
                return (
                  <>
                    <Row>
                      <Col xs={2}>
                        <Link to={`/admin/return-call/${call.id}`}>
                          View Return Call
                        </Link>
                      </Col>
                      <Col xs={10}>{call.returnCallFollowUpNotes}</Col>
                    </Row>
                  </>
                );
              })}
          </div>
        </Grid>
      </Form>
    </div>
  );
};

function handleChange(dispatch, event, formField) {
  let field = null;
  let value = null;
  if (["callStart", "callEnd", "returnCallDate"].includes(formField)) {
    field = formField;
    value = event._d;
  } else if (formField === "age") {
    field = formField;
    // we want to force integers in this numeric field
    value = event.target.value >= 1 ? Math.round(event.target.value) : 1;
  } else if (formField === "headTrauma") {
    field = formField;
    value = event ? event.map(normalizeHeadTrauma) : [];
  } else if (event.target) {
    field = event.target.name;
    value = event.target.value;
    value = value === "true" ? true : value;
    value = value === "false" ? false : value;
  } else {
    // select form items
    field = formField;
    value = event;
  }
  return dispatch({ type: UPDATE_QUESTIONNAIRE_FORM_FIELD, field, value });
}

export default Questionnaire;
